import {
    DEFAULT_PROJECT_COMMUNICATION_AUDIENCES,
    type CommunicationAudienceSelection,
    type CommunicationTemplateKey,
    type ContextualAudienceKey,
    type ProjectCommunicationAudience,
    type ProjectCommunicationAudienceConfig,
} from '../email/audience';

type FirestoreRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is FirestoreRecord => (
    value !== null && typeof value === 'object' && !Array.isArray(value)
);

const COMMUNICATION_TEMPLATE_KEYS: CommunicationTemplateKey[] = [
    'rehearsalReport',
    'meetingReport',
    'callSheet',
    'announcement',
    'deadlineReminder',
    'meetingInvite',
];

const CONTEXTUAL_AUDIENCE_KEYS = new Set<ContextualAudienceKey>([
    'meeting-attendees',
    'called-recipients',
    'deadline-department',
]);

function uniqueTrimmedStrings(value: unknown): string[] {
    if (!Array.isArray(value)) return [];

    const seen = new Set<string>();
    const normalized: string[] = [];

    const normalizedItems = value
        .filter((item): item is string => typeof item === 'string')
        .map(item => item.trim())
        .filter(Boolean);
    for (const item of normalizedItems) {
        if (!seen.has(item)) {
            seen.add(item);
            normalized.push(item);
        }
    }

    return normalized;
}

function optionalTrimmedString(value: unknown): string | undefined {
    if (typeof value !== 'string') return;

    const trimmed = value.trim();
    return trimmed ? trimmed : undefined;
}

function normalizeExternalAudienceRecipients(value: unknown): ProjectCommunicationAudience['externalRecipients'] {
    if (!Array.isArray(value)) return [];

    const seen = new Set<string>();
    const recipients: ProjectCommunicationAudience['externalRecipients'] = [];

    for (const item of value) {
        if (!isRecord(item)) continue;

        const email = typeof item.email === 'string' ? item.email.trim().toLowerCase() : '';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || seen.has(email)) {
            continue;
        }

        seen.add(email);
        const name = optionalTrimmedString(item.name);
        recipients.push(name ? { email, name } : { email });
    }

    return recipients;
}

function cloneAudienceSelection(selection: CommunicationAudienceSelection): CommunicationAudienceSelection {
    return {
        standingAudienceIds: [...selection.standingAudienceIds],
        contextualAudienceKeys: [...selection.contextualAudienceKeys],
    };
}

function normalizeStandingAudienceIds(value: unknown): string[] {
    const validAudienceIds = new Set(DEFAULT_PROJECT_COMMUNICATION_AUDIENCES.audiences.map(audience => audience.id));
    return uniqueTrimmedStrings(value).filter(id => validAudienceIds.has(id));
}

function normalizeContextualAudienceKeys(value: unknown): ContextualAudienceKey[] {
    return uniqueTrimmedStrings(value).filter((key): key is ContextualAudienceKey => (
        CONTEXTUAL_AUDIENCE_KEYS.has(key as ContextualAudienceKey)
    ));
}

function normalizeAudienceSelection(value: unknown, fallback: CommunicationAudienceSelection): CommunicationAudienceSelection {
    if (!isRecord(value)) {
        return cloneAudienceSelection(fallback);
    }

    return {
        standingAudienceIds: normalizeStandingAudienceIds(value.standingAudienceIds),
        contextualAudienceKeys: normalizeContextualAudienceKeys(value.contextualAudienceKeys),
    };
}

function cloneProjectCommunicationAudienceDefaults(): ProjectCommunicationAudienceConfig {
    return {
        audiences: DEFAULT_PROJECT_COMMUNICATION_AUDIENCES.audiences.map(audience => ({
            ...audience,
            manualIncludeContactIds: [...audience.manualIncludeContactIds],
            manualExcludeContactIds: [...audience.manualExcludeContactIds],
            externalRecipients: audience.externalRecipients.map(recipient => ({ ...recipient })),
        })),
        defaults: COMMUNICATION_TEMPLATE_KEYS.reduce((defaults, key) => {
            defaults[key] = cloneAudienceSelection(DEFAULT_PROJECT_COMMUNICATION_AUDIENCES.defaults[key]);
            return defaults;
        }, {} as ProjectCommunicationAudienceConfig['defaults']),
    };
}

export function normalizeProjectCommunicationAudiences(value: unknown): ProjectCommunicationAudienceConfig {
    if (!isRecord(value)) {
        return cloneProjectCommunicationAudienceDefaults();
    }

    const incomingAudiences = Array.isArray(value.audiences) ? value.audiences : [];
    const audiences = DEFAULT_PROJECT_COMMUNICATION_AUDIENCES.audiences.map(defaultAudience => {
        const incoming = incomingAudiences.find((candidate): candidate is FirestoreRecord => (
            isRecord(candidate) && candidate.id === defaultAudience.id
        ));
        const label = optionalTrimmedString(incoming?.label);
        const description = optionalTrimmedString(incoming?.description);
        const normalizedAudience: ProjectCommunicationAudience = {
            ...defaultAudience,
            manualIncludeContactIds: uniqueTrimmedStrings(
                incoming?.manualIncludeContactIds ?? incoming?.manualPersonnelIncludes
            ),
            manualExcludeContactIds: uniqueTrimmedStrings(
                incoming?.manualExcludeContactIds ?? incoming?.manualPersonnelExcludes
            ),
            externalRecipients: normalizeExternalAudienceRecipients(incoming?.externalRecipients),
        };

        if (label) {
            normalizedAudience.label = label;
        }
        if (description) {
            normalizedAudience.description = description;
        }
        if (typeof incoming?.disabled === 'boolean') {
            normalizedAudience.disabled = incoming.disabled;
        }

        return normalizedAudience;
    });
    const incomingDefaults = isRecord(value.defaults) ? value.defaults : {};

    return {
        audiences,
        defaults: COMMUNICATION_TEMPLATE_KEYS.reduce((defaults, key) => {
            defaults[key] = normalizeAudienceSelection(
                incomingDefaults[key],
                DEFAULT_PROJECT_COMMUNICATION_AUDIENCES.defaults[key]
            );
            return defaults;
        }, {} as ProjectCommunicationAudienceConfig['defaults']),
    };
}
